import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Target, ShoppingBag, Mail, Users, Lock, Heart, ArrowRight, ChevronDown, User, LogOut, LogIn, MessageCircle, Ticket, Facebook, Instagram, Youtube, Twitter, Linkedin, Globe, HelpCircle, Eye, Gamepad2 } from "lucide-react";
import { formatRoleLabel } from "@/lib/formatRole";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CookieBanner } from "@/components/CookieBanner";
import { TicketPurchaseDialog } from "@/components/TicketPurchaseDialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import DOMPurify from "dompurify";
import { Progress } from "@/components/ui/progress";
import gamesHeroImg from "@/assets/games-hero.png";

function NewsletterSubscribeButton({ ptaId, userEmail }: { ptaId: string; userEmail: string }) {
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (!ptaId || !userEmail) return;
    supabase
      .from('newsletter_subscribers')
      .select('is_subscribed')
      .eq('pta_id', ptaId)
      .eq('email', userEmail)
      .maybeSingle()
      .then(({ data }) => {
        setSubscribed(data?.is_subscribed ?? false);
        setLoading(false);
      });
  }, [ptaId, userEmail]);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      // Get parent record
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { data: parent } = await supabase
        .from('parents')
        .select('id')
        .eq('user_id', user.id)
        .eq('pta_id', ptaId)
        .maybeSingle();

      if (!parent) {
        toast({ title: "Error", description: "Parent record not found. Please register first.", variant: "destructive" });
        return;
      }

      const { error } = await supabase
        .from('newsletter_subscribers')
        .upsert({
          pta_id: ptaId,
          parent_id: parent.id,
          email: userEmail,
          is_subscribed: true,
          unsubscribed_at: null,
        }, { onConflict: 'pta_id,email' });

      if (error) throw error;
      setSubscribed(true);
      toast({ title: "Subscribed!", description: "You'll receive our newsletter updates." });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Button size="lg" disabled><Mail className="h-5 w-5 mr-2" />Loading...</Button>;
  
  if (subscribed) {
    return <p className="text-sm text-muted-foreground">✅ You're subscribed to our newsletter</p>;
  }

  return (
    <Button size="lg" className="gap-2" onClick={handleSubscribe}>
      <Mail className="h-5 w-5" />
      Subscribe to Newsletter
    </Button>
  );
}

export default function FundraisingPage() {
  const {
    toast
  } = useToast();
  const {
    ptaId
  } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ptaData, setPtaData] = useState<any>(null);
  const [sponsorshipData, setSponsorshipData] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [donateOpen, setDonateOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState("");
  const [members, setMembers] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [goals, setGoals] = useState<any[]>([]);
  const [marketplaceItems, setMarketplaceItems] = useState<any[]>([]);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [selectedMarketplaceItem, setSelectedMarketplaceItem] = useState<any>(null);
  const [ticketPurchaseEvent, setTicketPurchaseEvent] = useState<any>(null);
  const [socialLinks, setSocialLinks] = useState<any[]>([]);
  const [faqs, setFaqs] = useState<any[]>([]);
  const [donorName, setDonorName] = useState("");
  const [donationLoading, setDonationLoading] = useState(false);
  const [selectedGoalForDetails, setSelectedGoalForDetails] = useState<any>(null);
  const [stats, setStats] = useState({
    raisedThisYear: 0,
    activeGoals: 0,
    communityMembers: 0
  });
  useEffect(() => {
    fetchPTAData();
    checkUser();
  }, [ptaId]);

  useEffect(() => {
    if (user && ptaId) {
      fetchUnreadMessagesCount();
    }
  }, [user, ptaId]);
  
  useEffect(() => {
    // If no ptaId in URL but user is logged in, try to get from parent record
    if (!ptaId && user) {
      getParentPTA();
    }
  }, [user, ptaId]);
  
  useEffect(() => {
    if (ptaData?.id) {
      fetchEvents();
      fetchGoals();
      fetchMarketplaceItems();
      fetchStats();
      fetchSocialLinks();
      fetchFaqs();
    }
  }, [ptaData?.id]);
  
  const fetchStats = async () => {
    if (!ptaData?.id) return;
    
    const currentYear = new Date().getFullYear();
    
    // Get total raised this year from goals
    const { data: goalsData } = await supabase
      .from('goals')
      .select('current_amount')
      .eq('pta_id', ptaData.id)
      .gte('created_at', `${currentYear}-01-01`);
    
    const totalRaised = goalsData?.reduce((sum, g) => sum + (g.current_amount || 0), 0) || 0;
    
    // Get active goals count
    const { count: activeGoalsCount } = await supabase
      .from('goals')
      .select('*', { count: 'exact', head: true })
      .eq('pta_id', ptaData.id)
      .eq('status', 'active');
    
    // Get community members (parents) count
    const { count: parentCount } = await supabase
      .from('parents')
      .select('*', { count: 'exact', head: true })
      .eq('pta_id', ptaData.id);
    
    setStats({
      raisedThisYear: totalRaised,
      activeGoals: activeGoalsCount || 0,
      communityMembers: parentCount || 0
    });
  };
  
  const getParentPTA = async () => {
    const { data } = await supabase
      .from('parents')
      .select('pta_id, ptas!inner(id, name, school_name)')
      .eq('user_id', user?.id)
      .single();
    
    if (data?.pta_id) {
      // Redirect to correct URL with PTA ID
      navigate(`/fundraising/${data.pta_id}`);
    }
  };
  const checkUser = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    setUser(user);
  };

  const fetchUnreadMessagesCount = async () => {
    if (!user || !ptaId) return;

    try {
      // Get parent ID first
      const { data: parentData } = await supabase
        .from('parents')
        .select('id')
        .eq('user_id', user.id)
        .eq('pta_id', ptaId)
        .single();

      if (!parentData) return;

      // Note: shop_messages table structure needs to be checked
      // For now, set to 0 to avoid errors
      setUnreadMessagesCount(0);
    } catch (error) {
      console.error('Error fetching unread messages:', error);
    }
  };
  
  const fetchEvents = async () => {
    if (!ptaData?.id) return;
    
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('pta_id', ptaData.id)
      .eq('status', 'active')
      .order('event_date', { ascending: true })
      .limit(6);
    
    if (data) {
      setEvents(data);
    }
  };
  
  const fetchMarketplaceItems = async () => {
    if (!ptaData?.id) return;
    
    const { data, error } = await supabase
      .from('shop_items')
      .select('id, name, price, images, category')
      .eq('pta_id', ptaData.id)
      .eq('status', 'active')
      .eq('approval_status', 'approved')
      .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
      .order('created_at', { ascending: false })
      .limit(3);
    
    if (data) {
      setMarketplaceItems(data);
    }
  };
  
  const fetchGoals = async () => {
    if (!ptaData?.id) return;
    
    const { data, error } = await supabase
      .from('goals')
      .select('*')
      .eq('pta_id', ptaData.id)
      .eq('status', 'active')
      .eq('show_on_fundraising', true)
      .order('created_at', { ascending: false })
      .limit(6);
    
    if (data) {
      setGoals(data);
    }
  };

  const fetchSocialLinks = async () => {
    if (!ptaData?.id) return;
    
    const { data } = await supabase
      .from('pta_social_links')
      .select('*')
      .eq('pta_id', ptaData.id)
      .eq('is_active', true)
      .order('display_order', { ascending: true });
    
    if (data) {
      setSocialLinks(data);
    }
  };

  const fetchFaqs = async () => {
    if (!ptaData?.id) return;
    
    const { data } = await supabase
      .from('pta_faqs' as any)
      .select('*')
      .eq('pta_id', ptaData.id)
      .eq('is_published', true)
      .order('display_order', { ascending: true });
    
    if (data) {
      setFaqs(data as any[]);
    }
  };
  const fetchPTAData = async () => {
    if (!ptaId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    // Use RPC function to bypass RLS for anonymous users
    const {
      data: pta,
      error
    } = await supabase.rpc('get_public_pta_info', { p_pta_id: ptaId }).single();
    if (error || !pta) {
      setPtaData(null);
    } else {
      setPtaData(pta);
      if (pta.id) {
        const {
          data: sponsorship
        } = await supabase.from('sponsorships').select('*').eq('pta_id', pta.id).eq('status', 'active').eq('show_on_fundraising', true).order('created_at', {
          ascending: false
        }).limit(1).maybeSingle();
        setSponsorshipData(sponsorship);

        // Fetch team members via secure RPC (bypasses RLS, returns only safe data)
        const { data: teamData } = await supabase.rpc('get_pta_team_members', { p_pta_id: pta.id });
        
        // Filter out admin and custom roles from display
        const hiddenRoles = ['admin', 'custom'];
        const filteredTeamData = (teamData || []).filter((row: any) => !hiddenRoles.includes(row.role_name));

        if (filteredTeamData.length > 0) {
          // Group by member_name since one member can have multiple roles
          const grouped = filteredTeamData.reduce((acc: Record<string, any>, row: any) => {
            if (!acc[row.member_name]) {
              acc[row.member_name] = {
                member_name: row.member_name,
                avatar_url: row.avatar_url,
                show_avatar: row.show_avatar,
                roles: []
              };
            }
            acc[row.member_name].roles.push(row.role_name);
            return acc;
          }, {} as Record<string, any>);
          setMembers(Object.values(grouped));
        } else {
          setMembers([]);
        }
      }
    }
    setLoading(false);
  };
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    toast({
      title: "Signed out",
      description: "You have been signed out successfully"
    });
  };
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  const handleDonation = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const amount = parseFloat(selectedAmount.replace('£', ''));
    if (!amount || amount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid donation amount",
        variant: "destructive"
      });
      return;
    }

    if (!ptaData?.id) {
      toast({
        title: "Error",
        description: "PTA information not found",
        variant: "destructive"
      });
      return;
    }

    setDonationLoading(true);

    try {
      // Create income transaction in treasurer dashboard
      const { error } = await supabase
        .from('transactions')
        .insert({
          pta_id: ptaData.id,
          type: 'income',
          amount: amount,
          description: donorName ? `Donation from ${donorName}` : 'Anonymous donation',
          category: 'Donations',
          date: new Date().toISOString().split('T')[0],
          payment_method: 'online'
        });

      if (error) throw error;

      toast({
        title: "Thank you!",
        description: `Your donation of £${amount.toFixed(2)} has been received`
      });

      // Reset form
      setSelectedAmount("");
      setDonorName("");
      setDonateOpen(false);
      
      // Refresh stats
      fetchStats();
    } catch (error) {
      console.error('Error processing donation:', error);
      toast({
        title: "Error",
        description: "Failed to process donation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setDonationLoading(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>;
  }
  
  if (!ptaId && !user) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md mx-auto shadow-elevated">
          <CardHeader className="text-center">
            <CardTitle>Welcome to PTA Fundraising</CardTitle>
            <CardDescription>Please sign in to view your PTA's fundraising page</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/portal')}>
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>;
  }
  const headerColor = ptaData?.header_color || '#6366f1';
  const logoUrl = ptaData?.logo_url;
  const showMembers = ptaData?.show_meet_team_section !== false;
  const showContactDetails = ptaData?.show_contact_details !== false;
  const showFaqSection = ptaData?.show_faq_section !== false;
  const showSocialLinks = ptaData?.show_social_links !== false;
  const showEventsSection = ptaData?.show_events_section !== false;

  const sectionBgClass = (bg: string) => bg === 'grey' ? 'bg-gray-100' : 'bg-white';

  
  const ptaMembers = [{
    name: "Sarah Smith",
    role: "Chair",
    bio: "Passionate about creating opportunities for our children",
    email: "sarah@example.com"
  }, {
    name: "John Doe",
    role: "Treasurer",
    bio: "Ensuring transparency in all our finances",
    email: "john@example.com"
  }, {
    name: "Emma Wilson",
    role: "Secretary",
    bio: "Keeping everyone organized and informed",
    email: "emma@example.com"
  }];
  
  return <div className="min-h-screen bg-background">
      {/* Modern Header with Navigation */}
      <header 
        className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border"
        style={{ backgroundColor: ptaData?.header_color ? `${ptaData.header_color}` : undefined }}
      >
        <div className="container flex h-20 items-center justify-between">
          <div className="flex items-center gap-8">
            {/* Logo - show logo if exists, otherwise show icon + name */}
            <Link to={`/fundraising/${ptaId}`} className="flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt="PTA Logo" className="h-12 w-auto rounded object-cover" />
              ) : (
                <>
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                    <span className="text-lg">🏫</span>
                  </div>
                  <span className="font-semibold text-lg hidden sm:inline-block">
                    {ptaData?.name || 'PTA Fundraising'}
                  </span>
                </>
              )}
            </Link>

            {/* Centered Navigation */}
            <nav className="hidden md:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2">
              <button onClick={() => scrollToSection('home')} className="text-sm font-medium hover:text-primary transition-colors">
                Home
              </button>
              {user && (
                <>
                  <button onClick={() => scrollToSection('events')} className="text-sm font-medium hover:text-primary transition-colors">
                    Events
                  </button>
                  <button onClick={() => scrollToSection('goals')} className="text-sm font-medium hover:text-primary transition-colors">
                    Goals
                  </button>
                  {ptaData?.show_marketplace_section && (
                    <button onClick={() => navigate(`/marketplace?pta=${ptaId}`)} className="text-sm font-medium hover:text-primary transition-colors">
                      Marketplace
                    </button>
                  )}
                  {ptaData?.show_games_section && (
                    <button onClick={() => navigate(`/fundraising/${ptaId}/games`)} className="text-sm font-medium hover:text-primary transition-colors">
                      Games
                    </button>
                  )}
                </>
              )}
              <button onClick={() => scrollToSection('about')} className="text-sm font-medium hover:text-primary transition-colors">
                About
              </button>
            </nav>
          </div>

          {/* User Actions */}
          <div className="flex items-center gap-3">
            {user ? <>
                {/* Messages Icon - only show if marketplace enabled */}
                {ptaData?.show_marketplace_section && (
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="relative"
                    onClick={() => navigate(`/parent-profile?pta=${ptaId}&tab=messages`)}
                  >
                    <MessageCircle className="h-5 w-5" />
                    {unreadMessagesCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-semibold">
                        {unreadMessagesCount > 9 ? '9+' : unreadMessagesCount}
                      </span>
                    )}
                  </Button>
                )}
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={user.user_metadata?.avatar_url} alt={user.email} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {user.email?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="flex items-center justify-start gap-2 p-2">
                      <div className="flex flex-col space-y-1 leading-none">
                        <p className="font-medium text-sm">{user.email}</p>
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(`/parent-profile?pta=${ptaId}`)}>
                      <User className="mr-2 h-4 w-4" />
                      My Profile
                    </DropdownMenuItem>
                    {ptaData?.show_marketplace_section && (
                      <DropdownMenuItem onClick={() => navigate(`/marketplace/my-listings?pta=${ptaId}`)}>
                        <ShoppingBag className="mr-2 h-4 w-4" />
                        My Listings
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </> : <>
                <Button size="sm" className="gap-2" onClick={() => setDonateOpen(true)}>
                  <Heart className="h-4 w-4" />
                  Donate
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate(`/portal/${ptaId}`)}>
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign In
                </Button>
              </>}

            <Dialog open={donateOpen} onOpenChange={setDonateOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-2 hidden">
                  <Heart className="h-4 w-4" />
                  Donate
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Make a Donation</DialogTitle>
                  <DialogDescription>
                    Your donation helps support our school community
                  </DialogDescription>
                </DialogHeader>
                <form className="space-y-4" onSubmit={handleDonation}>
                  <div className="grid grid-cols-4 gap-2">
                    {['£5', '£10', '£25', '£50'].map(amount => <Button key={amount} type="button" variant={selectedAmount === amount ? "default" : "outline"} className="h-12" onClick={() => setSelectedAmount(amount)}>
                        {amount}
                      </Button>)}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="custom-amount">Custom Amount (£)</Label>
                    <Input id="custom-amount" type="number" placeholder="10.00" value={selectedAmount.replace('£', '')} onChange={e => setSelectedAmount(`£${e.target.value}`)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="donor-name">Your Name (Optional)</Label>
                    <Input id="donor-name" placeholder="John Smith" value={donorName} onChange={e => setDonorName(e.target.value)} />
                  </div>
                  <Button type="submit" className="w-full" size="lg" disabled={donationLoading}>
                    {donationLoading ? "Processing..." : "Complete Donation"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative overflow-hidden bg-gradient-hero py-20 md:py-32">
        {sponsorshipData && (
          <div className="absolute top-4 right-4 bg-background/95 backdrop-blur px-4 py-2 rounded-lg shadow-card">
            <p className="text-sm">
              <span className="text-muted-foreground">Sponsored by:</span>{' '}
              <span className="font-semibold text-primary">{sponsorshipData.business_name}</span>
            </p>
          </div>
        )}
        
        <div className="container">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Badge variant="secondary" className="text-sm mb-2">
              Fundraising Page
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
              {ptaData?.hero_welcome_text || 'Welcome to our PTA'}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {ptaData?.hero_description_text || 'Join our community in creating amazing opportunities for our children through fundraising, events, and parent involvement.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button size="lg" className="gap-2" onClick={() => setDonateOpen(true)}>
                <Heart className="h-5 w-5" />
                Donate Now
              </Button>
              <Button size="lg" variant="outline" className="gap-2" onClick={() => scrollToSection('events')}>
                View Events
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="container mt-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="text-center shadow-card">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-primary mb-2">£{stats.raisedThisYear.toLocaleString()}</div>
                <p className="text-sm text-muted-foreground">Raised This Year</p>
              </CardContent>
            </Card>
            <Card className="text-center shadow-card">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-primary mb-2">{stats.activeGoals}</div>
                <p className="text-sm text-muted-foreground">Active Goals</p>
              </CardContent>
            </Card>
            <Card className="text-center shadow-card">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-primary mb-2">{stats.communityMembers}</div>
                <p className="text-sm text-muted-foreground">Community Members</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Dynamically ordered sections: Events, Goals, Marketplace */}
      {user && (() => {
        const sections = [
          { key: 'events', order: ptaData?.events_section_order ?? 3, bg: ptaData?.events_section_bg ?? 'white' },
          { key: 'goals', order: ptaData?.goals_section_order ?? 2, bg: ptaData?.goals_section_bg ?? 'grey' },
          { key: 'marketplace', order: ptaData?.marketplace_section_order ?? 1, bg: ptaData?.marketplace_section_bg ?? 'white' },
          { key: 'games', order: ptaData?.games_section_order ?? 4, bg: ptaData?.games_section_bg ?? 'grey' },
          { key: 'meet_team', order: ptaData?.meet_team_section_order ?? 5, bg: ptaData?.meet_team_section_bg ?? 'white' },
          { key: 'contact', order: ptaData?.contact_section_order ?? 6, bg: ptaData?.contact_section_bg ?? 'grey' },
          { key: 'about', order: ptaData?.about_section_order ?? 7, bg: ptaData?.about_section_bg ?? 'grey' },
          { key: 'get_involved', order: ptaData?.get_involved_section_order ?? 8, bg: ptaData?.get_involved_section_bg ?? 'white' },
          { key: 'newsletter', order: ptaData?.newsletter_section_order ?? 9, bg: ptaData?.newsletter_section_bg ?? 'white' },
        ].sort((a, b) => a.order - b.order);

        return sections.map(({ key }) => {
          if (key === 'events' && showEventsSection) {
            return (
              <section key="events" id="events" className={`py-20 ${sectionBgClass(sections.find(s => s.key === 'events')?.bg || 'white')}`}>
                <div className="container">
                  <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">Upcoming Events</h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                      Join us at our upcoming events and be part of our vibrant school community
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                    {events.length > 0 ? events.map((event, i) => {
                      const isTicketed = event.is_ticketed;
                      const ticketPrice = event.ticket_price;
                      const ticketsSold = event.tickets_sold || 0;
                      const maxTickets = event.max_tickets;
                      const isSoldOut = maxTickets && ticketsSold >= maxTickets;
                      const showTicketAvailability = event.show_ticket_availability !== false;
                      const showFreeLabel = event.show_free_label !== false;
                      
                      return (
                        <Card key={i} className="shadow-card hover:shadow-elevated transition-shadow flex flex-col h-full">
                          <CardHeader className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <Calendar className="h-8 w-8 text-primary" />
                              <div className="flex flex-col items-end gap-1">
                                <Badge variant="secondary">{new Date(event.event_date).toLocaleDateString()}</Badge>
                                {isTicketed && (
                                  <>
                                    {isSoldOut ? (
                                      <Badge variant="destructive">Sold Out</Badge>
                                    ) : ticketPrice > 0 ? (
                                      <Badge variant="default">£{ticketPrice.toFixed(2)}</Badge>
                                    ) : showFreeLabel ? (
                                      <Badge variant="default">Free</Badge>
                                    ) : null}
                                    {showTicketAvailability && maxTickets && !isSoldOut && (
                                      <span className="text-xs text-muted-foreground">{maxTickets - ticketsSold} left</span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            <CardTitle className="text-xl">{event.name}</CardTitle>
                            <CardDescription>{event.description || 'Join us for this exciting event!'}</CardDescription>
                            {event.time && (
                              <p className="text-sm text-muted-foreground">Time: {event.time}</p>
                            )}
                            {event.location && (
                              <p className="text-sm text-muted-foreground">Location: {event.location}</p>
                            )}
                          </CardHeader>
                          <CardContent className="mt-auto">
                            {isTicketed ? (
                              <Button 
                                className="w-full gap-2" 
                                disabled={isSoldOut}
                                onClick={() => setTicketPurchaseEvent(event)}
                              >
                                <Ticket className="h-4 w-4" />
                                {isSoldOut ? "Sold Out" : "Get Tickets"}
                              </Button>
                            ) : (
                              <Button className="w-full" variant="outline">
                                Learn More
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      );
                    }) : <div className="col-span-full text-center py-12">
                        <p className="text-muted-foreground">No upcoming events at the moment. Check back soon!</p>
                      </div>}
                  </div>
                  
                  {ticketPurchaseEvent && ptaData?.id && (
                    <TicketPurchaseDialog
                      open={!!ticketPurchaseEvent}
                      onOpenChange={(open) => !open && setTicketPurchaseEvent(null)}
                      eventId={ticketPurchaseEvent.id}
                      eventTitle={ticketPurchaseEvent.name}
                      ptaId={ptaData.id}
                      namedTicketsRequired={ticketPurchaseEvent.named_tickets_required || false}
                      ticketingEnabled={ticketPurchaseEvent.is_ticketed || false}
                      onPurchaseComplete={() => {
                        setTicketPurchaseEvent(null);
                        fetchEvents();
                      }}
                    />
                  )}
                </div>
              </section>
            );
          }

          if (key === 'goals') {
            return (
              <section key="goals" id="goals" className={`py-20 ${sectionBgClass(sections.find(s => s.key === 'goals')?.bg || 'white')}`}>
                <div className="container">
                  <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">Fundraising Goals</h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                      See the impact we're making together and support the causes that matter
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                    {goals.length > 0 ? goals.map((goal, i) => {
                      const progress = goal.target_amount ? Math.round((goal.current_amount / goal.target_amount) * 100) : 0;
                      return <Card key={i} className="shadow-card flex flex-col h-full cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelectedGoalForDetails(goal)}>
                        <CardHeader>
                          <div className="flex items-center gap-2 mb-2">
                            <Target className="h-6 w-6 text-primary" />
                            <CardTitle className="text-xl">{goal.title}</CardTitle>
                          </div>
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-primary">£{goal.current_amount?.toFixed(0) || 0}</span>
                            <span className="text-sm text-muted-foreground">of £{goal.target_amount?.toFixed(0) || 0}</span>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4 flex-1 flex flex-col">
                          {goal.details && <p className="text-sm text-muted-foreground line-clamp-2">{goal.details}</p>}
                          <div className="space-y-2">
                            <div className="h-3 bg-secondary rounded-full overflow-hidden">
                              <div className="h-full bg-primary transition-all duration-500" style={{
                                width: `${Math.min(progress, 100)}%`
                              }} />
                            </div>
                            <p className="text-xs text-muted-foreground text-right">{progress}% funded</p>
                          </div>
                          {goal.target_date && (
                            <p className="text-xs text-muted-foreground">
                              Target: {new Date(goal.target_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          )}
                          <div className="flex gap-2 mt-auto">
                            <Button className="flex-1" onClick={(e) => { e.stopPropagation(); setDonateOpen(true); }}>
                              Support This Goal
                            </Button>
                          </div>
                        </CardContent>
                      </Card>;
                    }) : <div className="col-span-full text-center py-12">
                        <p className="text-muted-foreground">No active fundraising goals at the moment. Check back soon!</p>
                      </div>}
                  </div>
                </div>
              </section>
            );
          }

          if (key === 'marketplace' && ptaData?.show_marketplace_section) {
            return (
              <section key="marketplace" id="marketplace" className={`py-20 ${sectionBgClass(sections.find(s => s.key === 'marketplace')?.bg || 'white')}`}>
                <div className="container">
                  <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">Community Marketplace</h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                      Find great deals on school essentials from our community
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                    {marketplaceItems.length > 0 ? (
                      marketplaceItems.map((item) => (
                        <Card key={item.id} className="shadow-card hover:shadow-elevated transition-shadow">
                          <CardHeader>
                            <div className="w-full h-48 bg-muted/50 rounded-lg overflow-hidden mb-4">
                              {item.images && item.images.length > 0 ? (
                                <img 
                                  src={item.images[0]} 
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-6xl">
                                  📦
                                </div>
                              )}
                            </div>
                            <CardTitle className="text-lg line-clamp-2">{item.name}</CardTitle>
                            {item.price && (
                              <div className="text-2xl font-bold text-primary">
                                £{parseFloat(item.price).toFixed(2)}
                              </div>
                            )}
                            <Badge variant="outline" className="w-fit">{item.category}</Badge>
                          </CardHeader>
                          <CardContent>
                            <Button 
                              variant="outline" 
                              className="w-full gap-2"
                              onClick={() => setSelectedMarketplaceItem(item)}
                            >
                              <ShoppingBag className="h-4 w-4" />
                              View Details
                            </Button>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <div className="col-span-full text-center py-12 text-muted-foreground">
                        No items currently available in the marketplace
                      </div>
                    )}
                  </div>

                  <div className="text-center mt-8">
                    <Button variant="outline" size="lg" onClick={() => navigate(`/marketplace?pta=${ptaId}`)}>
                      View All Listings
                    </Button>
                  </div>
                </div>
              </section>
            );
          }

          if (key === 'games' && ptaData?.show_games_section) {
            return (
              <section key="games" id="games" className={`py-20 ${sectionBgClass(sections.find(s => s.key === 'games')?.bg || 'white')}`}>
                <div className="container">
                  <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">
                    <div className="flex-1 space-y-6 text-center md:text-left md:pl-12 lg:pl-20">
                      <h2 className="text-3xl md:text-4xl font-bold text-foreground">Games & Competitions</h2>
                      <p className="text-lg text-muted-foreground max-w-lg">
                        Try your luck and win exciting prizes! Pick numbers, guess names, and compete in fun challenges — every entry supports our school.
                      </p>
                      <Button size="lg" onClick={() => navigate(`/fundraising/${ptaId}/games`)} className="gap-2">
                        <Gamepad2 className="h-5 w-5" /> Play Now
                      </Button>
                    </div>
                    <div className="flex-1 flex justify-center">
                      <img src={gamesHeroImg} alt="Games and competitions" className="w-full max-w-md rounded-2xl shadow-lg" />
                    </div>
                  </div>
                </div>
              </section>
            );
          }

          if (key === 'meet_team' && showMembers && members.length > 0) {
            return (
              <section key="meet_team" className={`py-20 ${sectionBgClass(sections.find(s => s.key === 'meet_team')?.bg || 'white')}`}>
                <div className="container max-w-4xl">
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className="text-2xl font-bold mb-2 flex items-center justify-center gap-2">
                        <Users className="h-6 w-6 text-primary" />
                        Meet the Team
                      </h3>
                      <p className="text-muted-foreground">
                        The dedicated volunteers making it all happen
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {members.slice(0, 3).map((member, idx) => <Card key={member.member_name + idx} className="shadow-card text-center">
                          <CardContent className="pt-6">
                            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground text-2xl font-bold mb-4 overflow-hidden">
                              {member.show_avatar && member.avatar_url ? <img src={member.avatar_url} alt={member.member_name} className="w-full h-full object-cover" /> : <span>{member.member_name.split(' ').map((n: string) => n[0]).join('')}</span>}
                            </div>
                            <h4 className="font-semibold text-lg mb-1">{member.member_name}</h4>
                            <div className="flex flex-wrap gap-1 justify-center">
                              {member.roles?.map((role: string) => (
                                <Badge key={role} variant="secondary" className="mb-1">{formatRoleLabel(role)}</Badge>
                              ))}
                            </div>
                          </CardContent>
                        </Card>)}
                    </div>
                    {members.length > 3 && (
                      <div className="text-center mt-4">
                        <Button variant="outline" onClick={() => navigate(`/fundraising/${ptaId}/team`)}>
                          View All <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            );
          }

          if (key === 'contact' && showContactDetails && ptaData?.shared_email) {
            return (
              <section key="contact" className={`py-20 ${sectionBgClass(sections.find(s => s.key === 'contact')?.bg || 'white')}`}>
                <div className="container max-w-4xl">
                  <Card className="shadow-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5 text-primary" />
                        Get in Touch
                      </CardTitle>
                      <CardDescription>
                        Have questions? Contact us at <a href={`mailto:${ptaData.shared_email}`} className="text-primary hover:underline">{ptaData.shared_email}</a>
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="contact-name">Name</Label>
                            <Input id="contact-name" placeholder="Your name" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="contact-email">Email</Label>
                            <Input id="contact-email" type="email" placeholder="your@email.com" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="contact-message">Message</Label>
                          <Textarea id="contact-message" placeholder="How can we help?" rows={4} />
                        </div>
                        <Button type="submit" className="w-full">Send Message</Button>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              </section>
            );
          }

          if (key === 'about' && (ptaData?.show_about_section !== false)) {
            return (
              <section key="about" id="about" className={`py-20 ${sectionBgClass(sections.find(s => s.key === 'about')?.bg || 'grey')}`}>
                <div className="container max-w-4xl">
                  <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">About Our PTA</h2>
                    <p className="text-muted-foreground">
                      Learn more about who we are and what we do
                    </p>
                  </div>

                  <Card className="shadow-card mb-12">
                    <CardContent className="pt-6 space-y-4">
                      <div className="text-lg whitespace-pre-wrap">
                        {ptaData?.about_text || `Our PTA is dedicated to supporting ${ptaData?.school_name || 'our school'} and creating amazing opportunities for our children. We organize events, raise funds, and build a strong community where every parent can get involved.`}
                      </div>

                      {showContactDetails && <div className="pt-4 mt-4 border-t border-border space-y-2">
                          {ptaData?.charity_number && <p><strong>Charity Number:</strong> {ptaData.charity_number}</p>}
                          <p><strong>School:</strong> {ptaData?.school_name || 'N/A'}</p>
                          {ptaData?.shared_email && <p><strong>Email:</strong> {ptaData.shared_email}</p>}
                        </div>}
                    </CardContent>
                  </Card>
                </div>
              </section>
            );
          }

          if (key === 'get_involved' && (ptaData?.show_get_involved_section !== false)) {
            return (
              <section key="get_involved" className={`py-12 ${sectionBgClass(sections.find(s => s.key === 'get_involved')?.bg || 'white')}`}>
                <div className="container max-w-4xl mx-auto">
                  <Card className="shadow-elevated bg-gradient-hero border-0">
                    <CardContent className="pt-6 text-center">
                      <h3 className="text-2xl font-bold mb-2">Want to Get Involved?</h3>
                      <p className="text-muted-foreground mb-6">
                        Join our PTA and help make a difference in our school community
                      </p>
                      <Link to={`/become-a-member/${ptaId}`}>
                        <Button size="lg" className="gap-2">
                          <Users className="h-5 w-5" />
                          Learn How to Join
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              </section>
            );
          }

          if (key === 'newsletter' && (ptaData?.show_newsletter_section !== false)) {
            return (
              <section key="newsletter" className={`py-12 ${sectionBgClass(sections.find(s => s.key === 'newsletter')?.bg || 'white')}`}>
                <div className="container max-w-4xl mx-auto">
                  <Card className="shadow-elevated border-0">
                    <CardContent className="pt-6 text-center">
                      <Mail className="h-10 w-10 text-primary mx-auto mb-3" />
                      <h3 className="text-2xl font-bold mb-2">Stay Up to Date</h3>
                      <p className="text-muted-foreground mb-6">
                        Subscribe to our newsletter and never miss important updates from {ptaData?.name || 'our PTA'}
                      </p>
                      {user ? (
                        <NewsletterSubscribeButton ptaId={ptaData?.id} userEmail={user?.email} />
                      ) : (
                        <Link to={`/parent-portal/${ptaId}`}>
                          <Button size="lg" className="gap-2">
                            <Mail className="h-5 w-5" />
                            Register to Subscribe
                          </Button>
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </section>
            );
          }

          return null;
        });
      })()}

      {/* FAQ Section */}
      {showFaqSection && faqs.length > 0 && (
        <section id="faq" className="py-20 bg-background">
          <div className="container max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 flex items-center justify-center gap-2">
                <HelpCircle className="h-8 w-8 text-primary" />
                Frequently Asked Questions
              </h2>
              <p className="text-muted-foreground">
                Find answers to common questions about our PTA
              </p>
            </div>

            <Card className="shadow-card">
              <CardContent className="pt-6">
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={faq.id} value={`item-${index}`}>
                      <AccordionTrigger className="text-left">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-muted-foreground whitespace-pre-wrap">
                          {faq.answer}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="container">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="space-y-3">
              {logoUrl ? (
                <img src={logoUrl} alt="PTA Logo" className="h-12 w-auto rounded object-cover" />
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                    <span className="text-lg">🏫</span>
                  </div>
                  <span className="font-semibold">{ptaData?.name || 'PTA'}</span>
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                Supporting {ptaData?.school_name || 'our school'} community
              </p>
              {/* Social Media Links */}
              {showSocialLinks && socialLinks.length > 0 && (
                <div className="flex gap-3 mt-3">
                  {socialLinks.map((link) => {
                    const getSocialIcon = (platform: string) => {
                      switch (platform) {
                        case 'facebook': return <Facebook className="h-5 w-5" />;
                        case 'instagram': return <Instagram className="h-5 w-5" />;
                        case 'twitter': return <Twitter className="h-5 w-5" />;
                        case 'youtube': return <Youtube className="h-5 w-5" />;
                        case 'linkedin': return <Linkedin className="h-5 w-5" />;
                        default: return <Globe className="h-5 w-5" />;
                      }
                    };
                    return (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary transition-colors"
                        aria-label={`Visit our ${link.platform} page`}
                      >
                        {getSocialIcon(link.platform)}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-3">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => scrollToSection('events')} className="text-muted-foreground hover:text-primary transition-colors">
                    Events
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('goals')} className="text-muted-foreground hover:text-primary transition-colors">
                    Goals
                  </button>
                </li>
                <li>
              {ptaData?.show_marketplace_section && <button onClick={() => scrollToSection('marketplace')} className="text-muted-foreground hover:text-primary transition-colors">
                  Marketplace
                </button>}
                </li>
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-muted-foreground hover:text-primary transition-colors">
                    About
                  </button>
                </li>
              </ul>
            </div>

            {/* Get Involved */}
            <div>
              <h4 className="font-semibold mb-3">Get Involved</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => setDonateOpen(true)} className="text-muted-foreground hover:text-primary transition-colors">
                    Donate
                  </button>
                </li>
                <li>
                  <Link to={`/become-a-member/${ptaId}`} className="text-muted-foreground hover:text-primary transition-colors">
                    Become a Member
                  </Link>
                </li>
              </ul>
            </div>

            {/* Policies */}
            <div>
              <h4 className="font-semibold mb-3">Policies</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/cookies" className="text-muted-foreground hover:text-primary transition-colors">
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link to="/refund" className="text-muted-foreground hover:text-primary transition-colors">
                    Refund Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-foreground">
            <p>© {new Date().getFullYear()} The PTA Hub. All rights reserved.</p>
          </div>
        </div>
      </footer>
      
      {/* Marketplace Item Details Modal */}
      {selectedMarketplaceItem && (
        <Dialog open={!!selectedMarketplaceItem} onOpenChange={(open) => !open && setSelectedMarketplaceItem(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedMarketplaceItem.name}</DialogTitle>
              <DialogDescription>
                <Badge variant="outline" className="mt-2">{selectedMarketplaceItem.category}</Badge>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {selectedMarketplaceItem.images && selectedMarketplaceItem.images.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {selectedMarketplaceItem.images.map((img: string, idx: number) => (
                    <img 
                      key={idx}
                      src={img} 
                      alt={`${selectedMarketplaceItem.name} ${idx + 1}`}
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                  ))}
                </div>
              )}
              
              {selectedMarketplaceItem.price && (
                <div>
                  <h4 className="font-semibold mb-2">Price</h4>
                  <p className="text-2xl font-bold text-primary">
                    £{parseFloat(selectedMarketplaceItem.price).toFixed(2)}
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button 
                  className="flex-1"
                  onClick={() => {
                    setSelectedMarketplaceItem(null);
                    navigate(`/marketplace?pta=${ptaId}`);
                  }}
                >
                  View in Marketplace
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setSelectedMarketplaceItem(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      <CookieBanner />
    </div>;
}